import type { ReactElement } from 'react';
import { Info, PauseCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { useToast } from '@/hooks/use-toast';
import {
  isAgentAutoUpdateEnabled,
  useAgentAutoUpdateSetting,
  useUpdateAgentAutoUpdateSetting,
} from '@/hooks/api/useAgentAutoUpdate';
import { flagsmithFeatureFlags } from '@/lib/flagsmith';
import { Section } from './Section';

interface AgentAutoUpdateSectionProps {
  organizationId: string;
  /** Owners and Managers. Everyone else sees the setting read-only. */
  canModifySettings: boolean;
}

/**
 * KIR-5191: the "Automatic agent updates" switch on Settings → Organization.
 *
 * **Self-gating** on the `agent_auto_update` flag, so no props thread through
 * `OrganizationSettingsContent`. Owners and Managers change it; every other
 * role sees it read-only, like the other organization settings. On by
 * default: no stored setting means On.
 */
export function AgentAutoUpdateSection({
  organizationId,
  canModifySettings,
}: AgentAutoUpdateSectionProps): ReactElement | null {
  const { flags } = useFeatureFlags([flagsmithFeatureFlags.AGENT_AUTO_UPDATE]);
  const isFeatureEnabled =
    flags[flagsmithFeatureFlags.AGENT_AUTO_UPDATE] ?? false;
  const isVisible = isFeatureEnabled;

  const settingQuery = useAgentAutoUpdateSetting(organizationId, isVisible);
  const updateMutation = useUpdateAgentAutoUpdateSetting(organizationId);
  const { toast } = useToast();

  if (!isVisible) return null;

  const enabled = isAgentAutoUpdateEnabled(settingQuery.data);
  // A failed read must not show the On default as if it were saved.
  const loadFailed = settingQuery.isError;
  // Knostic's kill switch: no agent updates, whatever the org switch says.
  const pausedByKnostic = settingQuery.data?.pausedByKnostic === true;

  const handleChange = async (next: boolean): Promise<void> => {
    try {
      await updateMutation.mutateAsync(next);
      toast({
        title: 'Settings saved',
        description: next
          ? 'Automatic agent updates are on'
          : 'Automatic agent updates are off',
      });
    } catch {
      toast({
        variant: 'destructive',
        title: 'Failed to update',
        description: 'Could not save the automatic agent updates setting',
      });
    }
  };

  return (
    <Section
      title="Agent"
      description="Control how the Kirin agent on your organization's machines stays up to date"
    >
      <Card className="rounded-xl border-border/60 bg-card/80">
        <CardContent className="p-6">
          <div className="flex flex-col gap-6 sm:flex-row sm:items-start sm:justify-between">
            <div className="space-y-4">
              <div className="space-y-1.5">
                <h3 className="text-base font-semibold">
                  Automatic agent updates
                </h3>
                <p className="text-sm text-muted-foreground">
                  When on, every Kirin agent in your organization installs the
                  latest signed Kirin release by itself, with no action from
                  your developers. When off, agents stay on the version they
                  run.
                </p>
              </div>
              <div className="flex items-start gap-2 text-xs italic text-muted-foreground">
                <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <p>
                  Machines you manage with an MDM update too, unless your MDM
                  installs a fixed version. Those machines stay on that version
                  and show as Managed by MDM.
                </p>
              </div>
              {!canModifySettings && (
                <p className="text-xs text-muted-foreground">
                  Only Owners and Managers can change this setting.
                </p>
              )}
              {pausedByKnostic && (
                <Alert data-testid="agent-auto-update-paused">
                  <PauseCircle className="h-4 w-4" aria-hidden="true" />
                  <AlertDescription>
                    Knostic has paused automatic agent updates for now. Agents
                    keep their current version.
                  </AlertDescription>
                </Alert>
              )}
              {loadFailed && (
                <p role="alert" className="text-sm text-destructive">
                  Could not load this setting. Reload the page to try again.
                </p>
              )}
            </div>
            <div className="flex items-center gap-3 sm:pt-0.5">
              <Switch
                data-testid="agent-auto-update-toggle"
                checked={!loadFailed && enabled}
                onCheckedChange={next => void handleChange(next)}
                disabled={
                  !canModifySettings ||
                  loadFailed ||
                  settingQuery.isLoading ||
                  updateMutation.isPending
                }
                aria-label="Automatic agent updates"
              />
              <span
                className={`text-sm ${
                  !loadFailed && enabled
                    ? 'font-medium text-primary'
                    : 'text-muted-foreground'
                }`}
              >
                {loadFailed ? 'Unknown' : enabled ? 'On' : 'Off'}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>
    </Section>
  );
}

AgentAutoUpdateSection.displayName = 'AgentAutoUpdateSection';
